<template>
    <div class="min-h-screen flex items-center justify-center bg-gray-100">
        <div class="p-6 max-w-sm w-full bg-white shadow-lg rounded-lg">
            <h2 class="text-2xl font-bold mb-6 text-center">{{ title }}</h2>

            <div class="flex flex-col gap-4">
                <template v-if="mode === 'resetPassword'">
                    <label class="ui-input ui-input-bordered flex items-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            class="h-4 w-4 opacity-70"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <input
                            v-model="password"
                            type="password"
                            class="grow"
                            placeholder="New password"
                            :disabled="codeInvalid"
                            autocomplete="new-password"
                        />
                    </label>

                    <label class="ui-input ui-input-bordered flex items-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            class="h-4 w-4 opacity-70"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <input
                            v-model="password2"
                            type="password"
                            class="grow"
                            :disabled="codeInvalid"
                            placeholder="Confirm new password"
                            autocomplete="new-password"
                        />
                    </label>
                </template>

                <div
                    v-if="invalidFeedback"
                    role="alert"
                    class="ui-alert ui-alert-error"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 shrink-0 stroke-current"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span>{{ invalidFeedback }}</span>
                </div>

                <div
                    v-if="successFeedback"
                    role="alert"
                    class="ui-alert ui-alert-success"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 shrink-0 stroke-current"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span>{{ successFeedback }}</span>
                </div>

                <button
                    class="ui-btn bg-primary mt-4"
                    :disabled="!!successFeedback || codeInvalid"
                    @click="resetPassword"
                >
                    Set new password
                </button>

                <a class="ui-link ui-link-neutral mx-auto">
                    <router-link to="/login">Back to login</router-link>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { confirmPasswordReset, verifyPasswordResetCode, applyActionCode } from 'firebase/auth';
import { auth } from 'system/firebase.js';

export default {
    name: 'AuthAction',
    data() {
        return {
            mode: null,
            oobCode: null,
            password: '',
            password2: '',
            invalidFeedback: '',
            successFeedback: '',
            codeInvalid: false,
        };
    },
    computed: {
        title() {
            return this.mode === 'resetPassword' ? 'Set new password' : 'Email verification';
        },
    },
    async created() {
        const params = new URLSearchParams(window.location.search);

        this.mode = params.get('mode');
        this.oobCode = params.get('oobCode');

        if (!this.mode || !this.oobCode) {
            this.$router.push({ name: 'login' });
            return;
        }

        if (this.mode === 'verifyEmail') {
            await this.verifyEmail();
        } else if (this.mode === 'resetPassword') {
            try {
                await verifyPasswordResetCode(auth, this.oobCode);
            } catch {
                this.invalidFeedback = 'This reset link is invalid or has expired';
                this.codeInvalid = true;
            }
        }
    },
    methods: {
        async verifyEmail() {
            try {
                await applyActionCode(auth, this.oobCode);
                this.successFeedback = 'Email verified successfully';
            } catch {
                this.invalidFeedback = 'Email already verified or link has expired';
            } finally {
                setTimeout(() => {
                    this.$router.push({ name: 'today' });
                }, 3500);
            }
        },
        async resetPassword() {
            this.invalidFeedback = '';
            this.successFeedback = '';

            if (this.password !== this.password2) {
                this.invalidFeedback = 'Oops, passwords do not match';
                return;
            }

            try {
                await confirmPasswordReset(auth, this.oobCode, this.password);
                this.successFeedback = 'Password updated. You can now log in.';
                setTimeout(() => {
                    this.$router.push({ name: 'login' });
                }, 3500);
            } catch (error) {
                if (error.code === 'auth/weak-password') {
                    this.invalidFeedback = 'Oops, the provided password is weak';
                } else {
                    this.invalidFeedback = 'Oooops, error resetting password';
                    console.log(error);
                }
            }
        },
    },
};
</script>
